// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import loadAccountTiles from "@flip/App/Handlers/UserQuickLinks/LoadAccountTiles";
import { AccountTilesModel } from "@flip/App/Services/AccountService";
import { xHasRoles } from "@flip/App/Store/Selectors/SessionSelectors";
import { USER_ROLES } from "@flip/XDL/constants";
import AngleRight from "@flip/XDL/Icons/AngleRight";
import OpenInvoices from "@flip/XDL/Icons/OpenInvoices";
import PendingOrders from "@flip/XDL/Icons/PendingOrders";
import { fontConfig, headingsFontConfig } from "@flip/XDL/PreStyle/typography";
import { color, xScreen } from "@flip/XDL/Utilities";
import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getBillToState } from "@insite/client-framework/Store/Data/BillTos/BillTosSelectors";
import { useGetLink, useGetLinks } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import Icon, { IconPresentationProps, IconWrapper } from "@insite/mobius/Icon";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import * as React from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const enum fields {
    itemsList = "itemsList",
    buttonLabel = "buttonLabel",
    buttonLink = "buttonLink",
}

interface OwnProps {
    fields: {
        [fields.buttonLabel]: string;
        [fields.buttonLink]: LinkFieldValue;
        [fields.itemsList]: {
            fields: {
                title: string;
                imageUrl: string;
                linkTitle: string;
                link: LinkFieldValue;
                type: "numberOfPendingOrders" | "numberOfOpenInvoices";
                numberLabel: string;
            };
        }[];
    };
}

const mapDispatchToProps = {
    loadAccountTiles,
};

const mapStateToProps = (state: ApplicationState) => {
    const session = getSession(state);
    const billTo = getBillToState(state, session.billToId).value;
    const showSaleRep = xHasRoles(state, [USER_ROLES.BUYER1, USER_ROLES.BUYER2]);
    return {
        fullName: `${session.firstName || ""} ${session.lastName || ""}`,
        customerNum: billTo?.customerNumber,
        showSaleRep,
        salesperson: (session as any).salesperson,
    };
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps> & WidgetProps;

interface UserQuickLinksStyles {
    container?: InjectableCss;
    wrapper?: InjectableCss;
    infoSection?: InjectableCss;
    titleSection?: InjectableCss;
    infoAndActionSection?: InjectableCss;
    customerNumberSection?: TypographyPresentationProps;
    buttonSection?: ButtonPresentationProps;
    itemsSection?: InjectableCss;
    title?: TypographyPresentationProps;
    subtitle?: TypographyPresentationProps;
    blockContent?: InjectableCss;
    item?: {
        orderIcon?: IconPresentationProps;
        invoiceIcon?: IconPresentationProps;
        number?: TypographyPresentationProps;
        numberLabel?: TypographyPresentationProps;
        wrapper?: InjectableCss;
        lastSectionWrapper?: InjectableCss;
        title?: TypographyPresentationProps;
        link?: LinkPresentationProps;
    };
}

const UserQuickLinksStyles: UserQuickLinksStyles = {
    container: {
        css: css`
            padding: 24px 0 0 0;
            max-width: 882px;
            ${xScreen("lte", "md")} {
                max-width: 100%;
            }
        `,
    },
    infoSection: {
        css: css`
            flex-direction: column;
            padding: 20px 0 20px 20px;
            ${xScreen("lt", "sm")} {
                padding: 20px;
                margin-bottom: 16px;
            }
        `,
    },
    // XNG-Changes: FLIP-852
    infoAndActionSection: {
        css: css`
            display: flex;
            justify-content: space-between;
            align-items: baseline;
            ${xScreen("lt", "sm")} {
                flex-direction: column;
                align-items: start;
            }
        `,
    },
    itemsSection: {
        css: css`
            display: flex;
            gap: 22px;

            ${xScreen("lt", "sm")} {
                gap: 20px;
            }

            ${xScreen("lt", "sm")} {
                flex-direction: column;
                gap: 16px;
            }
        `,
    },
    titleSection: {
        css: css`
            margin-bottom: 8px;
            ${xScreen("lt", "sm")} {
                margin-bottom: 4px;
            }
        `,
    },
    title: {
        variant: "h4",
        color: "primary",
        css: css`
            margin-bottom: 4px;
        `,
    },
    // XNG-Changes: FLIP-852
    subtitle: {
        variant: "h1",
        weight: headingsFontConfig.weight.extraBold,
        css: css`
            align-self: center;
            ${xScreen("lt", "sm")} {
                margin-bottom: 4px;
                align-self: unset;
            }
        `,
    },
    customerNumberSection: {
        size: 16,
        lineHeight: "26px",
        weight: fontConfig.weight.semiBold,
        css: css`
            & > span {
                color: ${getColor("primary")};
            }
            ${xScreen("lt", "sm")} {
                margin-bottom: 12px;
            }
        `,
    },
    buttonSection: {
        sizeVariant: "large",
    },
    blockContent: {
        css: css`
            background-color: ${color("common.white")};
            border: 2px solid ${color("secondary")};
            border-radius: 24px;
            flex: 1 1 0;
            display: flex;
            flex-wrap: nowrap;
            overflow: hidden;
            height: 120px;
            width: 100%;

            ${xScreen("lte", "md")} {
                flex: 0 1 230px;
                height: 142px;
            }

            ${xScreen("lt", "sm")} {
                flex: 1 0 100%;
                height: 112px;
                width: 100%;
            }
        `,
    },
    item: {
        orderIcon: {
            src: PendingOrders,
            size: 56,
            css: css`
                ${xScreen("lte", "md")} {
                    display: none;
                }
            `,
        },
        invoiceIcon: {
            src: OpenInvoices,
            size: 56,
            css: css`
                ${xScreen("lte", "md")} {
                    display: none;
                }
            `,
        },
        number: {
            size: 28,
            fontFamily: headingsFontConfig.fontFamily,
            weight: headingsFontConfig.weight.bold,
            lineHeight: "34px",
            css: css`
                margin-right: 7px;
            `,
        },
        numberLabel: {
            size: 14,
            fontFamily: fontConfig.fontFamily,
            lineHeight: "22px",
        },
        wrapper: {
            css: css`
                padding: 20px;
                display: flex;
                justify-content: flex-start;
                width: 100%;
                ${xScreen("lte", "md")} {
                    flex-direction: column;
                }
            `,
        },
        lastSectionWrapper: {
            css: css`
                display: flex;
                flex-direction: column;
                justify-content: space-between;

                ${xScreen("gte", "lg")} {
                    margin-left: 30px;
                }

                ${xScreen("lt", "sm")} {
                    flex-direction: row;
                    align-items: baseline;
                    justify-content: start;
                }
            `,
        },
        title: {
            variant: "h4",
            forwardAs: "h2",
            css: css`
                margin-bottom: 0;
                margin-left: 16px;
                max-width: 100px;
                ${xScreen("lte", "md")} {
                    max-width: 100%;
                    margin-bottom: 12px;
                    margin-left: 0;
                }
            `,
        },
        link: {
            color: "text.link",
            hoverStyle: {
                color: "common.ui8",
            },
            icon: {
                iconProps: {
                    src: AngleRight,
                    size: 13,
                    color: "text.link",
                },
                position: "right",
            },
            typographyProps: {
                size: 14,
                lineHeight: "22px",
                weight: fontConfig.weight.semiBold,
                css: css`
                    letter-spacing: 0;
                `,
            },
            css: css`
                justify-content: flex-end;
                ${IconWrapper} {
                    margin-left: 8px;
                }
                &:focus {
                    outline: none;
                }
                align-items: center;
                ${xScreen("lte", "md")} {
                    margin-top: 8px;
                }
                ${xScreen("lt", "sm")} {
                    margin-left: 16px;
                }
            `,
        },
    },
};

const styles = UserQuickLinksStyles;

const UserQuickLinks: React.FC<Props> = ({
    fields,
    fullName,
    customerNum,
    showSaleRep,
    salesperson,
    loadAccountTiles,
}) => {
    const [data, setData] = React.useState<AccountTilesModel>();
    const buttonLink = useGetLink(fields.buttonLink);
    const itemsLinks = useGetLinks(fields.itemsList ?? [], o => o.fields.link);
    const handleViewDashboardClicked = () => {
        window.location.href = buttonLink?.url || "/";
    };

    React.useEffect(() => {
        loadAccountTiles({
            onSuccess: result => {
                setData(result);
            },
            onError: error => {},
        });
    }, []);

    if (!data) {
        return null;
    }
    return (
        <StyledWrapper {...styles.container} data-test-selector="UserQuickLinks">
            <StyledWrapper {...styles.infoSection}>
                <StyledWrapper {...styles.titleSection}>
                    <Typography {...styles.title} data-test-selector="UserQuickLinks_title">
                        {translate("Hello")},
                    </Typography>
                </StyledWrapper>
                <StyledWrapper {...styles.infoAndActionSection}>
                    <Typography {...styles.subtitle} data-test-selector="UserQuickLinks_subtitle">
                        {fullName}
                    </Typography>
                    {customerNum && (
                        <Typography
                            {...styles.customerNumberSection}
                            data-test-selector="UserQuickLinks_customerNumber"
                        >
                            {translate("Customer #")} <Typography>{customerNum}</Typography>
                        </Typography>
                    )}
                    <Button
                        {...styles.buttonSection}
                        onClick={handleViewDashboardClicked}
                        aria-label={fields.buttonLabel}
                    >
                        {fields.buttonLabel}
                    </Button>
                </StyledWrapper>
            </StyledWrapper>
            <StyledWrapper {...styles.itemsSection}>
                {fields.itemsList?.map((itemField, index) => {
                    if (data[itemField.fields.type] === -1) {
                        return;
                    }
                    const item = {
                        type: itemField.fields.type,
                        title: itemField.fields.title,
                        numOfAvailable: data[itemField.fields.type] || 0,
                        linkTitle: itemField.fields.linkTitle,
                        numberLabel: itemField.fields.numberLabel,
                        link: itemsLinks?.[index].url || "",
                    };

                    return (
                        <StyledWrapper {...styles.blockContent} key={`${item.title}_${index}`}>
                            <StyledWrapper {...styles.item?.wrapper}>
                                <StyledWrapper>
                                    {item.type === "numberOfPendingOrders" && <Icon {...styles.item?.orderIcon} />}
                                    {item.type === "numberOfOpenInvoices" && <Icon {...styles.item?.invoiceIcon} />}
                                </StyledWrapper>
                                <Typography {...styles.item?.title}>{item.title}</Typography>
                                <StyledWrapper {...styles.item?.lastSectionWrapper}>
                                    <StyledWrapper>
                                        <Typography {...styles.item?.number}>{item.numOfAvailable}</Typography>
                                        <Typography {...styles.item?.numberLabel}>{item.numberLabel}</Typography>
                                    </StyledWrapper>
                                    <Link href={item.link} {...styles.item?.link} aria-label={item.linkTitle}>
                                        {item.linkTitle}
                                    </Link>
                                </StyledWrapper>
                            </StyledWrapper>
                        </StyledWrapper>
                    );
                })}
            </StyledWrapper>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(UserQuickLinks),
    definition: {
        group: "Xngage Custom" as any,
        fieldDefinitions: [
            {
                name: fields.buttonLabel,
                editorTemplate: "TextField",
                displayName: "Button Label",
                fieldType: "General",
                defaultValue: "View My Dashboard",
            },
            {
                name: fields.buttonLink,
                displayName: "Button Link",
                editorTemplate: "LinkField",
                fieldType: "General",
                defaultValue: { type: "Page", value: "" },
            },
            {
                name: "itemsList",
                editorTemplate: "ListField",
                getDisplay: (item: HasFields) => {
                    return item.fields.title;
                },
                defaultValue: [],
                fieldType: "General",
                isVisible: item => !item.fields.isAuthenticated,
                fieldDefinitions: [
                    {
                        name: "type",
                        displayName: "Type",
                        editorTemplate: "DropDownField",
                        options: [
                            { displayName: "Pending Orders", value: "numberOfPendingOrders" },
                            { displayName: "Open Invoices", value: "numberOfOpenInvoices" },
                        ],
                        hideEmptyOption: true,
                        defaultValue: "numberOfPendingOrders",
                    },
                    {
                        name: "title",
                        editorTemplate: "TextField",
                        defaultValue: "",
                        isRequired: true,
                    },
                    {
                        name: "linkTitle",
                        editorTemplate: "TextField",
                        defaultValue: "",
                        isRequired: true,
                    },
                    {
                        name: "link",
                        editorTemplate: "LinkField",
                        defaultValue: { type: "Url", value: "" },
                        allowCategories: item => false,
                    },
                    {
                        name: "numberLabel",
                        editorTemplate: "TextField",
                        defaultValue: "",
                        isRequired: true,
                    },
                ],
            },
        ],
    },
};

export default widgetModule;
